const DASH = '—'
const MB = 1024 * 1024
const DAY_MS = 24 * 60 * 60 * 1000

export const EVIDENCE_MAX_FILES = 10
export const EVIDENCE_MAX_FILE_BYTES = 2 * MB
export const EVIDENCE_MAX_TOTAL_BYTES = 10 * MB

const STATUS_LABELS: Record<string, string> = {
  WAITING_MERCHANT_EVIDENCES: 'Aguardando suas provas',
  WAITING_ACQUIRER_ANALYSIS: 'Em análise pela adquirente',
  WAITING_ISSUER: 'Em análise pelo banco emissor',
  WON: 'Ganha',
  LOST: 'Perdida',
  REVERSED: 'Revertida',
  MERCHANT_EVIDENCE_DEADLINE_EXPIRED: 'Prazo para provas expirado',
  DEADLINE_EXPIRED: 'Prazo expirado',
}

const REASON_LABELS: Record<string, string> = {
  FRAUD: 'Fraude',
  PROCESSING_ERROR: 'Erro de processamento',
  COMMERCIAL_DISPUTE: 'Desacordo comercial',
  AUTHORIZATION: 'Autorização',
}

const CYCLE_LABELS: Record<string, string> = {
  FIRST: '1º ciclo',
  SECOND: '2º ciclo',
}

const EVENT_LABELS: Record<string, string> = {
  FIRST_CHARGEBACK: 'Chargeback recebido',
  FIRST_CHARGEBACK_REVERSED: 'Chargeback revertido',
  DISPUTE_NOTIFIED: 'Disputa notificada',
  MERCHANT_EVIDENCE_RECEIVED: 'Provas recebidas',
  EVIDENCE_ANALYSIS_ACCEPTED: 'Provas aceitas',
  EVIDENCE_ANALYSIS_DENIED: 'Provas negadas',
  LOST_DISPUTE: 'Disputa perdida',
}

const STATUS_COLORS: Record<string, 'warning' | 'info' | 'success' | 'error'> =
  {
    WAITING_MERCHANT_EVIDENCES: 'warning',
    WAITING_ACQUIRER_ANALYSIS: 'info',
    WAITING_ISSUER: 'info',
    WON: 'success',
    REVERSED: 'success',
    LOST: 'error',
    MERCHANT_EVIDENCE_DEADLINE_EXPIRED: 'error',
    DEADLINE_EXPIRED: 'error',
  }

// Valores novos da Stone caem no valor cru
const label = (map: Record<string, string>, value?: string | null) =>
  value ? (map[value] ?? value) : DASH

export const disputeStatusLabel = (status?: string | null) =>
  label(STATUS_LABELS, status)
export const disputeReasonLabel = (reason?: string | null) =>
  label(REASON_LABELS, reason)
export const disputeCycleLabel = (cycle?: string | null) =>
  label(CYCLE_LABELS, cycle)
export const disputeEventLabel = (type?: string | null) =>
  label(EVENT_LABELS, type)

export const disputeStatusColor = (status?: string | null) =>
  (status && STATUS_COLORS[status]) || 'default'

const brasiliaFormatter = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
})

export const formatBrasiliaParts = (iso?: string | null) => {
  if (!iso) return null
  const parts = Object.fromEntries(
    brasiliaFormatter.formatToParts(new Date(iso)).map((p) => [p.type, p.value])
  )
  return {
    date: `${parts.day}/${parts.month}/${parts.year}`,
    time: `${parts.hour}:${parts.minute}`,
  }
}

export const formatBrasilia = (iso?: string | null) => {
  const parts = formatBrasiliaParts(iso)
  return parts ? `${parts.date} ${parts.time}` : DASH
}

export const daysUntil = (iso?: string | null, now: Date = new Date()) => {
  if (!iso) return 0
  return Math.ceil((new Date(iso).getTime() - now.getTime()) / DAY_MS)
}

// Mesma regra do onlyOpen do back; usada quando canSubmitEvidence não vale (visão do admin)
export const isAwaitingEvidence = (
  dispute: {
    status: string
    responseDeadline: string | null
    evidenceSentAt?: string | null
  },
  now: Date = new Date()
) =>
  dispute.status === 'WAITING_MERCHANT_EVIDENCES' &&
  !dispute.evidenceSentAt &&
  daysUntil(dispute.responseDeadline, now) > 0

export const remainingDaysLabel = (days: number) => {
  if (days <= 0) return 'Prazo encerrado'
  return days === 1 ? 'Falta 1 dia' : `Faltam ${days} dias`
}

export const formatMegabytes = (bytes: number) =>
  (bytes / MB).toFixed(1).replace('.', ',')

export type TEvidenceValidation = {
  fileErrors: Record<number, string>
  fileWarnings: Record<number, string>
  globalErrors: string[]
  totalBytes: number
  valid: boolean
}

const isPdf = (file: File) =>
  file.name.toLowerCase().endsWith('.pdf') && file.type === 'application/pdf'

const SAFE_NAME = /^[A-Za-z0-9._ -]+$/

export const validateEvidenceFiles = (files: File[]): TEvidenceValidation => {
  const fileErrors: Record<number, string> = {}
  const fileWarnings: Record<number, string> = {}
  const globalErrors: string[] = []
  let totalBytes = 0

  files.forEach((file, index) => {
    totalBytes += file.size
    if (!isPdf(file)) {
      fileErrors[index] = 'Somente arquivos PDF são aceitos'
    } else if (file.size > EVIDENCE_MAX_FILE_BYTES) {
      fileErrors[index] = 'Cada arquivo deve ter até 2 MB'
    }
    if (!SAFE_NAME.test(file.name)) {
      fileWarnings[index] = 'Evite acentos e caracteres especiais no nome'
    }
  })

  if (files.length === 0) globalErrors.push('Envie ao menos um arquivo PDF')
  if (files.length > EVIDENCE_MAX_FILES)
    globalErrors.push('Envie no máximo 10 arquivos')
  if (totalBytes > EVIDENCE_MAX_TOTAL_BYTES)
    globalErrors.push('Arquivos excedem 10 MB no total')

  return {
    fileErrors,
    fileWarnings,
    globalErrors,
    totalBytes,
    valid: globalErrors.length === 0 && Object.keys(fileErrors).length === 0,
  }
}

const fileKey = (file: File) => `${file.name}|${file.size}|${file.lastModified}`

export const mergeEvidenceFiles = (current: File[], added: File[]) => {
  const keys = new Set(current.map(fileKey))
  const merged = [...current]
  added.forEach((file) => {
    const key = fileKey(file)
    if (!keys.has(key)) {
      keys.add(key)
      merged.push(file)
    }
  })
  return merged
}

const FINAL_ERRORS = [
  'Provas já enviadas para esta disputa',
  'Prazo para contestação encerrado',
  'Esta disputa não aceita mais contestação',
]

export const isFinalDisputeError = (message: string) =>
  FINAL_ERRORS.some((final) => message.includes(final))
